/**
 * "@Image1"-style mentions in generation prompts.
 *
 * Venice's Seedance reference mode lets the prompt point at attached reference
 * images by position ("@Image1, @Image2, …"). These helpers are pure so the
 * caret/token logic can be unit-tested without a DOM.
 */

export const imageTag = (index: number): string => `@Image${index + 1}`;

const TAG_RE = /@image(\d+)/gi;
/** A token is "@" preceded by start-of-text or whitespace, followed by word characters. */
const MENTION_AT_CARET_RE = /(?:^|\s)(@[\w-]*)$/;

export interface MentionToken {
    /** Index of the "@" in the value. */
    start: number;
    /** Text typed after the "@" (may be empty). */
    query: string;
}

/** The "@…" token the caret is currently inside/at the end of, if any. */
export const findMentionAtCaret = (value: string, caret: number): MentionToken | null => {
    const upto = value.slice(0, Math.max(0, caret));
    const m = MENTION_AT_CARET_RE.exec(upto);
    if (!m) return null;
    const token = m[1];
    return { start: upto.length - token.length, query: token.slice(1) };
};

export interface MentionCandidate {
    index: number;
    tag: string;
    label: string;
}

/**
 * Candidates for the picker, filtered by the typed query (matches tag number or label).
 * `tagOf` names each reference; Kling's reference mode, for one, has @Element and @Image tags.
 */
export const filterMentionCandidates = (query: string, labels: (string | undefined)[], tagOf: (index: number) => string = imageTag): MentionCandidate[] => {
    const q = query.trim().toLowerCase();
    const numeric = /^\d+$/.test(q);
    return labels
        .map((label, index) => ({ index, tag: tagOf(index), label: label || `Reference ${index + 1}` }))
        .filter(c => {
            if (!q) return true;
            // "@2" means the reference tagged 2, never a filename that happens to contain a 2
            if (numeric) return (/\d+$/.exec(c.tag)?.[0] ?? '').startsWith(q);
            return c.tag.toLowerCase().includes(q) || c.label.toLowerCase().includes(q);
        });
};

/** Replace the token at `mention` with `tag` (plus a trailing space) and return the new value + caret. */
export const insertMention = (value: string, mention: MentionToken, caret: number, tag: string): { value: string; caret: number } => {
    const before = value.slice(0, mention.start);
    const after = value.slice(Math.max(caret, mention.start));
    const inserted = `${tag} `;
    return { value: before + inserted + after, caret: before.length + inserted.length };
};

/** Insert a tag at an arbitrary caret (e.g. clicking a reference thumbnail), padding with spaces as needed. */
export const insertTagAt = (value: string, caret: number, tag: string): { value: string; caret: number } => {
    const at = Math.min(Math.max(caret, 0), value.length);
    const before = value.slice(0, at);
    const after = value.slice(at);
    const lead = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
    const trail = after.length > 0 && !/^\s/.test(after) ? ' ' : (after.length === 0 ? ' ' : '');
    const inserted = `${lead}${tag}${trail}`;
    return { value: before + inserted + after, caret: before.length + inserted.length };
};

/** Tag numbers used in the prompt that have no attached reference (1-based). */
export const findUnknownImageTags = (prompt: string, referenceCount: number): number[] => {
    const seen = new Set<number>();
    for (const m of prompt.matchAll(TAG_RE)) {
        const n = parseInt(m[1], 10);
        if (Number.isFinite(n) && (n < 1 || n > referenceCount)) seen.add(n);
    }
    return Array.from(seen).sort((a, b) => a - b);
};

/** Venice documents the tags as "@Image1"; accept any casing the user typed. */
export const normalizeImageTags = (prompt: string): string => prompt.replace(TAG_RE, (_, n: string) => `@Image${parseInt(n, 10)}`);

const REFERENCE_TAG_RE = /@(image|element|video|audio)(\d+)/gi;
const tagCase = (kind: string, n: string) => `@${kind[0].toUpperCase()}${kind.slice(1).toLowerCase()}${parseInt(n, 10)}`;

/** Tags used in the prompt that name no attached reference, written as Venice documents them (e.g. "@Element3"). */
export const findUnknownReferenceTags = (prompt: string, known: string[]): string[] => {
    const have = new Set(known.map(tag => tag.toLowerCase()));
    const unknown = new Map<string, string>();
    for (const m of prompt.matchAll(REFERENCE_TAG_RE)) {
        const tag = tagCase(m[1], m[2]);
        if (!have.has(tag.toLowerCase())) unknown.set(tag.toLowerCase(), tag);
    }
    return Array.from(unknown.values());
};

/** "@image1" and "@ELEMENT2" as Venice documents them for Kling: "@Image1", "@Element2". */
export const normalizeReferenceTags = (prompt: string): string =>
    prompt.replace(REFERENCE_TAG_RE, (_, kind: string, n: string) => tagCase(kind, n));

export const videoTag = (index: number): string => `@Video${index + 1}`;
export const audioTag = (index: number): string => `@Audio${index + 1}`;

const SEEDANCE_KIND: Record<string, string> = { image: 'Image', video: 'Video', audio: 'Audio' };

/**
 * Seedance names its references in the prompt as `<Image 1>`, `<Video 1>`, `<Audio 1>`: angle
 * brackets, a capital, one space before the number, and nothing else, as Venice's guide
 * says the syntax is exact. People type "@Image1", "@video2" or a near miss such as
 * "<video1>"; all of them are written the way Seedance reads them.
 * https://docs.venice.ai/guides/media/seedance-2-0
 */
export const toSeedanceTags = (prompt: string): string =>
    prompt
        .replace(/@(image|video|audio)(\d+)/gi, (_, kind: string, n: string) => `<${SEEDANCE_KIND[kind.toLowerCase()]} ${parseInt(n, 10)}>`)
        .replace(/<\s*(image|video|audio)\s*(\d+)\s*>/gi, (_, kind: string, n: string) => `<${SEEDANCE_KIND[kind.toLowerCase()]} ${parseInt(n, 10)}>`);

// ── Tags already in a prompt ─────────────────────────────────────────────────

/** A reference tag where it stands in a prompt, written as Venice documents it. */
export interface TagSpan {
    start: number;
    end: number;
    tag: string;
}

/** Every reference tag in a prompt (@Image1, @Element2, @Video1, @Audio1), in order; "@Image12x" is no tag. */
export const referenceTagSpans = (value: string): TagSpan[] =>
    Array.from(value.matchAll(/@(image|element|video|audio)(\d+)(?!\w)/gi), m => ({
        start: m.index ?? 0,
        end: (m.index ?? 0) + m[0].length,
        tag: tagCase(m[1], m[2]),
    }));

/** The tag the caret is in, or right against (a click on its first or last letter counts). */
export const referenceTagAt = (value: string, caret: number): TagSpan | null =>
    referenceTagSpans(value).find(span => caret >= span.start && caret <= span.end) ?? null;

/** One tag swapped for another; the caret goes just after it. */
export const replaceTag = (value: string, span: Pick<TagSpan, 'start' | 'end'>, tag: string): { value: string; caret: number } => ({
    value: value.slice(0, span.start) + tag + value.slice(span.end),
    caret: span.start + tag.length,
});
